import { getUniqueNumber } from "@highway/mbs-utils";
import { atom, PrimitiveAtom, useAtom } from "jotai";
import { ReactNode } from "react";
import { hwyStore } from "../../utilities/store";

export type TSnackType = "success" | "error" | "loading" | "custom";
export interface ISnack {
    id: number;
    message: ReactNode;
    action: ReactNode;
    type: TSnackType;
    duration: number;
}
export const centerSnacksAtom = atom<ISnack[]>([]);
export const rightSnacksAtom = atom<ISnack[]>([]);

type TSnackQueue = PrimitiveAtom<ISnack[]>;

const createSnack = (snack: Partial<ISnack>): ISnack => ({
    id: getUniqueNumber(),
    message: "",
    action: "",
    type: "success",
    duration: snack.type === "loading" ? 1_000_000 : 5_000,
    ...snack,
});

/**
 * Imperative snackbar API, for code that isn't a component -- module-level subscriptions, plain
 * utilities, anything that needs to raise a snack without a place to call a hook. It writes to the
 * same queues `useSnackbar` reads from, so these snacks are ordinary snacks in every way.
 *
 * Inside a component, prefer `useSnackbar`.
 */
export const snackbar = {
    add: (snack: Partial<ISnack>, queue: TSnackQueue = centerSnacksAtom) => {
        const newSnack = createSnack(snack);
        hwyStore.set(queue, (snacks) => [...snacks, newSnack]);
        return newSnack;
    },
    /** Returns false when that snack is no longer in the queue, so callers can raise a new one. */
    update: (id: number, updates: Partial<ISnack>, queue: TSnackQueue = centerSnacksAtom) => {
        const exists = hwyStore.get(queue).some((snack) => snack.id === id);
        if (exists) {
            hwyStore.set(queue, (snacks) =>
                snacks.map((snack) => (snack.id === id ? { ...snack, ...updates, id: snack.id } : snack)),
            );
        }
        return exists;
    },
    remove: (id: number, queue: TSnackQueue = centerSnacksAtom) =>
        hwyStore.set(queue, (snacks) => snacks.filter((snack) => snack.id !== id)),
};

const useSnackbar = (snackQueue: TSnackQueue = centerSnacksAtom) => {
    const [snacks, setSnacks] = useAtom(snackQueue, { store: hwyStore });

    /**
     * Adds a new snack notification to the `snacks` array.
     *
     * @param {Partial<ISnack>} snack - An object with partial properties of `ISnack` that
     * defines the attributes of the snack to be added. Missing properties will be populated
     * by `createSnack`, which generates a complete snack object.
     *
     * @returns {ISnack} The newly created snack object, including any default values added by `createSnack`.
     *
     * Example:
     * ```javascript
     * addSnack({ message: "New notification" });
     *
     * const snack = addSnack({ message: "New notification" });
     * ```
     */
    const addSnack = (snack: Partial<ISnack>) => snackbar.add(snack, snackQueue);

    /**
     * Removes a specific snack notification from the `snacks` array by its `id`.
     *
     * @param {number} id - The unique identifier of the snack to remove.
     *
     * This function should only be used in rare cases where a snack needs to be removed
     * based on a specific action, such as user-triggered events (e.g., an "Undo" action).
     * It filters out the snack with the matching `id` from the array and updates the state.
     *
     * Example:
     * ```javascript
     * const snackID = getUniqueNumber();
     * // Remove the snack with the specified id based on an action
     * addSnack({ id: snackID, message: "New notification" })
     * removeSnack(snackID);
     * ```
     * * ```javascript
     * // Remove the snack with the specified id based on an action
     * const snack = addSnack({ id: snackID, message: "New notification" })
     * removeSnack(snack.id);
     * ```
     */
    const removeSnack = (id: number) => snackbar.remove(id, snackQueue);

    /**
     * Updates an existing snack in place, leaving its position in the queue untouched.
     *
     * @param {number} id - The unique identifier of the snack to update.
     * @param {Partial<ISnack>} updates - The properties to merge into the existing snack.
     *
     * Use this instead of removing and re-adding a snack when only its contents change --
     * re-adding restarts the enter animation and moves the snack to the back of the queue.
     * Returns false if that snack has already left the queue, and changes nothing.
     *
     * Example:
     * ```javascript
     * const snack = addSnack({ message: "Export in progress...", type: "loading" });
     * updateSnack(snack.id, { message: "2 exports in progress..." });
     * ```
     */
    const updateSnack = (id: number, updates: Partial<ISnack>) => snackbar.update(id, updates, snackQueue);

    return { snacks, setSnacks, addSnack, removeSnack, updateSnack };
};

export default useSnackbar;
